import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import {
  Box,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Application, ApplicationStatus } from '../../data/types';
import { compactMoney, date, money } from '../../lib/format';
import { HOLDER_TYPE_LABELS } from '../../lib/labels';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { AboutThisPage, PageHeader, StatusChip } from '../../ui/primitives';
import { statusLabel } from '../../ui/statusTones';
import ChecklistDots from './ChecklistDots';

const COLUMNS: ApplicationStatus[] = [
  'INVITED',
  'IN_PROGRESS',
  'SUBMITTED',
  'KYC_REVIEW',
  'AWAITING_FUNDS',
  'PENDING_APPROVAL',
  'ISSUED',
];

function ApplicationCard({ application }: { application: Application }) {
  const navigate = useNavigate();
  const scope = useTenantScope();
  return (
    <Paper
      variant="outlined"
      onClick={() => navigate(`/manager/applications/${application.id}`)}
      onKeyDown={(event) =>
        event.key === 'Enter' && navigate(`/manager/applications/${application.id}`)
      }
      tabIndex={0}
      role="link"
      sx={{ p: 1.5, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
        {application.applicantName}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        {scope.fundById.get(application.fundId)?.code} ·{' '}
        {HOLDER_TYPE_LABELS[application.applicantType]}
        {application.allocationId ? ' · from allocation' : ''}
      </Typography>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {money(application.amount)}
        </Typography>
        <ChecklistDots checklist={application.checklist} />
      </Stack>
    </Paper>
  );
}

export default function ApplicationsPage() {
  const scope = useTenantScope();
  const navigate = useNavigate();
  const [view, setView] = useState<'board' | 'list'>('board');
  const [params] = useSearchParams();
  const [fundId, setFundId] = useState(params.get('fund') ?? 'ALL');
  const applications = useMemo(
    () =>
      scope.applications.filter((application) => fundId === 'ALL' || application.fundId === fundId),
    [scope.applications, fundId],
  );
  return (
    <>
      <PageHeader
        title="Applications"
        subtitle="From expression of interest to units issued. Investors complete applications in the app; the registry team reviews exceptions only."
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              select
              size="small"
              label="Fund"
              value={fundId}
              onChange={(event) => setFundId(event.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="ALL">All funds</MenuItem>
              {scope.funds.map((fund) => (
                <MenuItem key={fund.id} value={fund.id}>
                  {fund.code}
                </MenuItem>
              ))}
            </TextField>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_event, next) => next && setView(next)}
            >
              <ToggleButton value="board" aria-label="Board view">
                <ViewKanbanOutlinedIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list" aria-label="List view">
                <ViewListOutlinedIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        }
      />
      <AboutThisPage>
        <p>
          An application is the formal contract for an investment entity, based on that
          entity&apos;s allocation in the fund opportunity (allocations are made in the admin
          portal). Start one from the entity&apos;s allocation under Holders or Allocations. The
          investor completes entity details, beneficial owners, identity verification, TFN,
          FATCA/CRS self-certification, bank details, wholesale certificate and e-signature in the
          app.
        </p>
        <p>The dots show the eight checklist items: green done, amber pending, red needs review.</p>
      </AboutThisPage>
      {view === 'board' ? (
        <Box
          sx={{
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: 'minmax(220px, 1fr)',
            gap: 1.5,
            overflowX: 'auto',
            pb: 1,
          }}
        >
          {COLUMNS.map((status) => {
            const items = applications.filter((application) => application.status === status);
            return (
              <Box
                key={status}
                sx={{ bgcolor: '#F1F5F9', borderRadius: 2, p: 1.25, minHeight: 320 }}
              >
                <Stack direction="row" justifyContent="space-between" sx={{ px: 0.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {statusLabel(status)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {items.length} · {compactMoney(sumBy(items, (item) => item.amount))}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {items.map((application) => (
                    <ApplicationCard key={application.id} application={application} />
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Paper variant="outlined">
          <DataTable
            rows={applications}
            rowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/manager/applications/${row.id}`)}
            defaultSort={{ key: 'created', direction: 'desc' }}
            dense
            columns={[
              {
                key: 'ref',
                label: 'Reference',
                sortValue: (row) => row.reference,
                render: (row) => row.reference,
              },
              {
                key: 'name',
                label: 'Applicant',
                sortValue: (row) => row.applicantName,
                render: (row) => row.applicantName,
              },
              { key: 'fund', label: 'Fund', render: (row) => scope.fundById.get(row.fundId)?.code },
              {
                key: 'amount',
                label: 'Amount',
                align: 'right',
                sortValue: (row) => row.amount,
                render: (row) => money(row.amount),
              },
              {
                key: 'checklist',
                label: 'Checklist',
                render: (row) => <ChecklistDots checklist={row.checklist} />,
              },
              {
                key: 'created',
                label: 'Created',
                sortValue: (row) => row.createdAt,
                render: (row) => date(row.createdAt),
              },
              {
                key: 'status',
                label: 'Status',
                sortValue: (row) => row.status,
                render: (row) => <StatusChip status={row.status} />,
              },
            ]}
          />
        </Paper>
      )}
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
        Rejected and withdrawn:{' '}
        {
          applications.filter((item) => item.status === 'REJECTED' || item.status === 'WITHDRAWN')
            .length
        }
      </Typography>
    </>
  );
}
